/* eslint-disable class-methods-use-this */
/**
 * Dropdown selection for options
 */
class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.formParent = this.closest('product-form');
      this.form = this.formParent.querySelector('form') || null;
      this.formType = this.formParent.dataset.format || null;
        this.variant_json = this.querySelector('[data-variantJSON]');
        this.metalTone = this.querySelector('[data-metaltone]');
        this.container = this.formType === 'product-card' ? this.closest('[data-product-card]') : this.closest('[data-product-container]');
        /** @type {string|undefined} Last serialized value of the Gemstone/Gemstones option; used to skip re-rendering when other options change. */
        this._lastGemstoneOptionRaw = undefined;
    }

    connectedCallback() {
      this.updateSwatchLabel();
      if (this.formType === 'product-page') this.onVariantChange('load');

      this.addEventListener('change', (evt) => {
        const target = this.getInputForEventTarget(evt.target);
        
        // Clear validation error for the changed option
        if (target.tagName === 'SELECT') {
          const errorDiv = target.parentElement.querySelector('.validation-error');
          if (errorDiv) errorDiv.classList.add('d-none');
        } else if (target.tagName === 'INPUT') {
          const fieldset = target.closest('fieldset');
          if (fieldset) {
            const errorDiv = fieldset.querySelector('.validation-error');
            if (errorDiv) errorDiv.classList.add('d-none');
          }
          // Handle metal type and metal tone
          const container = target.closest('[data-optionindex="metalType"], [data-optionindex="metalTone"]');
          if (container) {
            const errorDiv = container.querySelector('.validation-error');
            if (errorDiv) errorDiv.classList.add('d-none');
          }
        }

        this.updateSelectedOptionLabel(target);

        eventBus.publish(PUB_SUB_EVENTS.optionChange, {
          data: {
            target,
            selectedOptionsId: this.optionValues,
          }
        });
        this.onVariantChange();
      });

      this.manageMetalSelection();

      const gemstoneFieldset = this.querySelector('fieldset[data-gemstone-option]');
      if (gemstoneFieldset) {
        gemstoneFieldset.addEventListener('change', () => {
          this.maybeUpdateBirthstoneAccordion();
        });
      }

      this.maybeUpdateBirthstoneAccordion();
    }

    validateOptions() {
      let isValid = true;
      
      // Remove any existing validation errors
      this.querySelectorAll('.validation-error').forEach(error => error.classList.add('d-none'));

      // Check metal type if present
      const metalTypeFieldset = this.querySelector('[data-metal-swatches]');
      if (metalTypeFieldset) {
        const hasMetalTypeSelection = metalTypeFieldset.querySelector('input[type="radio"]:checked');
        const errorDiv = metalTypeFieldset.querySelector('.validation-error');
        if (!hasMetalTypeSelection) {
          isValid = false;
          if (errorDiv) {
            errorDiv.classList.remove('d-none');
          }
        } else if (errorDiv) {
          errorDiv.classList.add('d-none');
        }
      }

      // Check variant radio button groups
      this.querySelectorAll('fieldset[data-optionindex]:not([data-optionindex="ring_size"])').forEach(fieldset => {
        const hasSelection = fieldset.querySelector('input[type="radio"]:checked');
        const errorDiv = fieldset.querySelector('.validation-error');
        if (!hasSelection) {
          isValid = false;
          if (errorDiv) {
            errorDiv.classList.remove('d-none');
          }
        } else if (errorDiv) {
          errorDiv.classList.add('d-none');
        }
      });

      // Check variant select elements
      this.querySelectorAll('select.variant_selector').forEach(select => {
        const errorDiv = select.parentElement.querySelector('.validation-error');
        if (!select.value) {
          isValid = false;
          if (errorDiv) {
            errorDiv.classList.remove('d-none');
            errorDiv.scrollIntoView({ behavior: "smooth", block: "start"});
          }
        } else if (errorDiv) {
          errorDiv.classList.add('d-none');
        }
      });

      // Check closure options if present
      const closerFieldset = this.querySelector('fieldset[data-optionindex="closer"]');
      if (closerFieldset) {
        const hasCloserSelection = closerFieldset.querySelector('input[type="radio"]:checked');
        const errorDiv = closerFieldset.querySelector('.validation-error');
        if (!hasCloserSelection) {
          isValid = false;
          if (errorDiv) {
            errorDiv.classList.remove('d-none');
            errorDiv.scrollIntoView({ behavior: "smooth", block: "start"});
          }
        } else if (errorDiv) {
          errorDiv.classList.add('d-none');
        }
      }

      // Check ring size if present
      const ringSizeSelect = this.querySelector('fieldset[data-optionindex="ring_size"]');
      if (ringSizeSelect) {
        const hasSelection = ringSizeSelect.querySelector('select');
        const errorDiv = ringSizeSelect.querySelector('.validation-error');
        if (!hasSelection.value) {
          isValid = false;
          if (errorDiv) {
            errorDiv.classList.remove('d-none');
            errorDiv.scrollIntoView({ behavior: "smooth", block: "end"});
          }
        } else if (errorDiv) {
          errorDiv.classList.add('d-none');
        }
      }

      // Gemstone slots: require a chosen birthstone per slot when the accordion markup is present (depends only on gemstone count, not other variant options).
      const birthstoneContainer = this.querySelector('[data-opt-birthstone-container]');
      if (birthstoneContainer && birthstoneContainer.querySelectorAll('input[type="hidden"][data-birthstone-slot]').length) {
        const birthErr = birthstoneContainer.closest('[data-product-option-picker]')?.querySelector('[data-birthstone-validation]');
        const hiddens = birthstoneContainer.querySelectorAll('input[data-birthstone-slot]');
        let stonesOk = true;

        for (const h of hiddens) {
          if (!h.value || !String(h.value).trim()) {
            stonesOk = false;
            break;
          }
        }

        if (!stonesOk) {
          isValid = false;
          if (birthErr) {
            birthErr.classList.remove('d-none');
            birthErr.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        } else if (birthErr) {
          birthErr.classList.add('d-none');
        }
      }

      return isValid;
    }

    updateSwatchLabel(){
      const colorSwatchContainer = this.querySelector('.color-swatch');
      if(colorSwatchContainer){
        const colorSwatches = colorSwatchContainer.querySelectorAll('.swatch');
        colorSwatches.forEach(swatch => {
          const input = swatch.querySelector('input[type="radio"]');
          const colorHandle = input.dataset.handle;
          const swatchStyle = Utility.getSwatchStyle(colorHandle);
          swatch.querySelector('.swatch-label').setAttribute('style', swatchStyle);
        });
      }
    }

    /**
     * Runs after any option change. Resolves the matching variant and syncs global state. Gemstone birthstone UI is driven separately by {@link maybeUpdateBirthstoneAccordion} (gemstone fieldset `change` + initial sync).
     */
    onVariantChange() {
      this.setCurrentVariant();
      eventBus.publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          container: this.container,
          variant: this.currentVariant,
        },
      });

      if (this.formType === 'product-page' && this.currentVariant) 
        globalSpace.product.currentVariant = this.currentVariant;
      if(typeof this.formParent?.updateProductDetails == 'function'){
        this.formParent.updateProductDetails(this.container, this.currentVariant);
      }
    }

    get selectedOptionValues() {
      return Array.from(this.querySelectorAll('select.variant_selector, fieldset input[type="radio"]:checked')).map(({ value }) => value);
    }


    /**
     * Selected value for the Gemstone/Gemstones option from the checked radio in [data-gemstone-option], not from `currentVariant`.
     * @returns {string}
     */
    getSelectedGemstoneOptionValue() {
      const input = this.querySelector('[data-gemstone-option] input[type="radio"]:checked');
      if (!input) return '';
      return input ? String(input.value) : '';
    }

    /**
     * Parses the numeric “how many stones” count from a variant option value (e.g. `"3"`, `"5 Stones"`).
     * @param {string|number|null|undefined} raw
     * @returns {number} Integer between 0 and 12 (cap).
     */
    parseGemstoneCount(raw) {
      if (raw == null || raw === '') return 0;
      const m = String(raw).trim().match(/\d+/);
      if (!m) return 0;
      const n = parseInt(m[0], 10);
      return Number.isFinite(n) ? Math.min(Math.max(n, 0), 12) : 0;
    }

    /**
     * Runs when the gemstone fieldset’s value changes (or on initial connect). Compares the selected radio value to {@link _lastGemstoneOptionRaw};
     * skips re-render if unchanged so other variant options do not reset the birthstone UI.
     */
    maybeUpdateBirthstoneAccordion() {
      const container = this.querySelector('[data-opt-birthstone-container]');
      if (!container) return;

      const raw = this.getSelectedGemstoneOptionValue();
      const normalized = raw == null ? '' : String(raw);

      if (normalized === this._lastGemstoneOptionRaw) {return;}

      this._lastGemstoneOptionRaw = normalized;
      this.updateBirthstoneAccordion();
    }

    /**
     * Rebuilds birthstone accordion markup from the gemstone fieldset’s selected value (via {@link getSelectedGemstoneOptionValue}), not from `currentVariant`.
     * Clears the container when the count is zero or the fieldset is missing.
     */
    updateBirthstoneAccordion() {
      const container = this.querySelector('[data-opt-birthstone-container]');
      if (!container) return;

      const birthErr = container.closest('[data-product-option-picker]')?.querySelector('[data-birthstone-validation]');
      if (birthErr) birthErr.classList.add('d-none');

      if (!this.querySelector('[data-gemstone-option]')) {
        container.innerHTML = '';
        return;
      }

      const raw = this.getSelectedGemstoneOptionValue();
      const count = this.parseGemstoneCount(raw);
      const labelTemplate = container.dataset.stoneLabelTemplate;
      const selectStonePlaceholder = container.dataset.selectStoneLabel || '';

      if (count < 1) {
        container.innerHTML = '';
        return;
      }
      container.innerHTML = this.generateBirthstoneHTML(count, labelTemplate, selectStonePlaceholder);
      this._bindBirthstoneSelection(container);
    }

    /**
     * Attaches click handlers to each birthstone picker button: sets the hidden line-item property input for that slot, updates the summary
     * preview, and clears the validation message for that fieldset.
     * @param {HTMLElement} container Root `[data-opt-birthstone-container]`
     */
    _bindBirthstoneSelection(container) {
      container.querySelectorAll('[data-birthstone-trigger]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();

          const name = btn.dataset.stoneName || '';
          const month = btn.dataset.stoneMonth || '';
          const image = btn.dataset.stoneImage || '';
          const wrapper = btn.closest('.birthstone-item-wrapper');
          const details = wrapper?.querySelector('details');
          const slot = details?.dataset.birthstoneSlot;

          if (!wrapper || !details || !slot) return;

          const hidden = wrapper.querySelector(`input[data-birthstone-slot="${slot}"]`);

          if (hidden) hidden.value = `${name} - ${month}`;

          wrapper.querySelectorAll('[data-birthstone-trigger]').forEach((b) => b.classList.remove('is-selected'));

          btn.classList.add('is-selected');

          const summaryInner = wrapper.querySelector('[data-birthstone-summary]');

          if (summaryInner) {
            let slotLabel = '';

            try {
              slotLabel = decodeURIComponent(details.dataset.birthstoneSlotLabel || '');
            } catch {
              slotLabel = '';
            }

            if (!slotLabel) {
              slotLabel = `Stone ${slot}`;
            }

            summaryInner.replaceChildren();

            if (image) {
              const imgEl = document.createElement('img');

              imgEl.src = image;
              imgEl.alt = '';
              imgEl.width = 48;
              imgEl.height = 48;
              imgEl.loading = 'lazy';
              imgEl.className = 'rounded align-middle';
              summaryInner.appendChild(imgEl);
            }

            const content = document.createElement('div');

            content.className = 'summery-content d-flex flex-column gap-0';

            const slotSpan = document.createElement('span');

            slotSpan.className = 'birthstone-slot-number fw-bold';
            slotSpan.textContent = slotLabel;

            const nameSpan = document.createElement('span');

            nameSpan.className = 'birthstone-name align-middle fs-md';
            nameSpan.textContent = `${name} - ${month}`;

            content.appendChild(slotSpan);
            content.appendChild(nameSpan);
            summaryInner.appendChild(content);
          }

          const birthErrInner = wrapper.closest('fieldset')?.querySelector('[data-birthstone-validation]');

          if (birthErrInner) birthErrInner.classList.add('d-none');
        });
      });
    }

    /**
     * Maps `month` text (or 1–12) to 0–11 for January–December; unknown values sort last.
     * @param {string|null|undefined} month
     * @returns {number}
     */
    _monthOrderIndex(month) {
      if (month == null || month === '') return 12;

      const s = String(month).toLowerCase().trim();
      const asNum = parseInt(s, 10);

      if (Number.isFinite(asNum) && asNum >= 1 && asNum <= 12 && String(asNum) === s.replace(/^0+/, '')) {
        return asNum - 1;
      }

      const full = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
      for (let i = 0; i < 12; i++) {
        if (s.includes(full[i])) return i;
      }
      const abbr = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      for (let i = 0; i < 12; i++) {
        if (s.startsWith(abbr[i])) return i;
      }

      return 12;
    }

    /**
     * Builds HTML for each stone slot: a `<details>` accordion, a hidden `properties[Stone N]` input for cart line properties, and a list of
     * catalog buttons from `globalSpace.birthstone`.
     * @param {number} stoneCount Number of slots (from the gemstone option value).
     * @param {string} [labelTemplate] From Liquid `data-stone-label-template` (must include `__NUM__` placeholders where the slot index goes).
     * @param {string} [selectStonePlaceholder] Default summary copy (e.g. “Select stone”) from `data-select-stone-label`.
     * @returns {string}
     */
    generateBirthstoneHTML(stoneCount, labelTemplate, selectStonePlaceholder) {
      const catalogRaw = Array.isArray(window.globalSpace?.birthstone) ? window.globalSpace.birthstone : [];
      const catalog = [...catalogRaw].sort((a, b) => {
        const ai = this._monthOrderIndex(a.month);
        const bi = this._monthOrderIndex(b.month);

        if (ai !== bi) return ai - bi;

        return String(a.stone_name || '').localeCompare(String(b.stone_name || ''));
      });
      const tmpl = labelTemplate ?? '';
      const placeholder = selectStonePlaceholder ?? '';
      const formId = this.form?.id;

      let html = '';

      for (let i = 0; i < stoneCount; i++) {
        const slot = i + 1;
        const summaryLabel = tmpl.replace(/__NUM__/g, String(slot));

        const buttons = catalog.map((item) => {
          const sn = item.stone_name || '';
          const mo = item.month || '';
          const img = item.image || '';

          return `
            <li class="birthstone-picker-item">
              <button type="button" class="birthstone-item-button" data-birthstone-trigger data-stone-month="${String(mo)}" data-stone-name="${String(sn)}" ${img ? `data-stone-image="${String(img)}"` : ''}>
                <div class="stone-image">
                  ${img ? `<img src="${img}" alt="${sn}" width="120" height="120" loading="lazy" />` : ''}
                </div>
                <span class="stone-name d-none">${sn}</span>
                <span class="stone-month d-block">${mo}</span>
              </button>
            </li>`;
        }).join('');

        const slotLabelAttr = encodeURIComponent(summaryLabel);
        const contentPanelId = formId ? `${formId}-birthstone-panel-${slot}` : `birthstone-panel-${slot}`;

        html += `
        <div class="birthstone-item-wrapper">
          <details class="birthstone-item" data-birthstone-slot="${slot}" data-birthstone-slot-label="${slotLabelAttr}">
            <summary class="birthstone-item-summary d-flex w-100 align-items-center justify-content-between gap-2">
              <div class="align-items-center justify-content-between birthstone-summary-inner d-flex gap-2 position-relative" data-birthstone-summary="" role="term" aria-details="${contentPanelId}">
                <div class="summery-content d-flex flex-column gap-0">
                  <span class="birthstone-slot-number fw-bold">${summaryLabel}</span>
                  <span class="birthstone-name align-middle fs-md">${placeholder}</span>
                </div>
              </div>
              <span class="icon-collapsible"></span>
            </summary>
            </details>
          <div class="birthstone-item-content" id="${contentPanelId}">
            <input type="hidden" name="properties[Stone ${slot}]" form="${this.dataset.formId}" value="" data-birthstone-slot="${slot}" />
            <div class="birthstone-item-content-inner overflow-hidden">
              <ul class="birthstone-item-list list-unstyled mt-3 mt-md-4">
                ${buttons}
              </ul>
            </div>
          </div>
        </div>
        `;
      }

      return html;
    }

    /**
     * change value of currentVariant when variant being changed 
     */
    setCurrentVariant() {
      this.currentVariant = false;
      const options = this.selectedOptionValues;
      const variants = this._getVariantData();
      let metalSwatchIndex = this.querySelector('[data-metal-swatches]')?.dataset?.optionindex;
      metalSwatchIndex = metalSwatchIndex - 1;
      if(metalSwatchIndex && metalSwatchIndex >= 1){
        const selectedMetal = options[metalSwatchIndex];
        options.splice(metalSwatchIndex, 1);
        options.splice(0, 0, selectedMetal);
      }

      variants.find(variant => {
        if (variant.options.every((option, i) => options[i] === option)) {
          this.currentVariant = variant;
        }
      });
    }

    /**
     * Update selected option label immediately when an option is changed
     */
    updateSelectedOptionLabel(target) {
      if (target.tagName === 'SELECT' || target.tagName === 'OPTION') {
        let selectedOption;
        if (target.tagName == 'OPTION') {
          selectedOption = target;
          if(target.closest('select').classList.contains('ring-form-select')){
            this.updateSelectedRingSize(selectedOption);
          }
        } else {
          selectedOption = target.selectedOptions[0];
          if(target.classList.contains('ring-form-select')){
            this.updateSelectedRingSize(selectedOption);
          }
        }
        if (selectedOption) {
          const label = target.closest('fieldset').querySelector('.selected-option');
          if (label) label.innerHTML = selectedOption.dataset.valueLabel;
        }
      } else if (target.tagName === 'INPUT') {
        const fieldset = target.closest('fieldset');
        if(fieldset) {
          const selectedOption = fieldset.querySelector('input[type="radio"]:checked');
          if (selectedOption) {
            let selectedValue = selectedOption.dataset.valueLabel
            const label = fieldset.querySelector('.selected-option');
            if (label) label.innerHTML = selectedValue;

            if(selectedOption.closest('[data-metal-swatches]')){
              if(selectedOption.dataset.tone?.includes('Gold')){
                this.metalTone.disabled = false;
                this.metalTone.value = selectedOption.dataset.tone;
              }else {
                this.metalTone.value = '';
                this.metalTone.disabled = true;
              }
            }
          }
        }
      }
    }

    updateSelectedRingSize(target){
      const ringSizeContent = document.querySelector('#product-ringsize-content');
      if(ringSizeContent){
        if(target.value){
          ringSizeContent.querySelector('[data-ring-size-value]').innerText = target.value;
          ringSizeContent.querySelector('[data-ring-size-diameter]').innerText = target.dataset.insideSize;
          ringSizeContent.querySelector('[data-ring-size-details]').classList.remove('d-none');
          ringSizeContent.querySelector('[data-selected-ring-size]').classList.add('d-none');
        }else{
          ringSizeContent.querySelector('[data-selected-ring-size]').classList.remove('d-none');
          ringSizeContent.querySelector('[data-ring-size-details]').classList.add('d-none');
        }
      }
    }

    /**
     * Store the all the variants json
     */
    _getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.variant_json?.textContent);
      return this.variantData;
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }
    
    get optionValues() {
      return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
        ({ dataset }) => dataset.optionValueId
      );
    }

    manageMetalSelection(){
      const metalButtons = this.querySelectorAll('.metal-selection-btn');
      const swatchInputs = this.querySelectorAll('.metal-swatch [data-metaltype]');
      metalButtons.forEach(btn => {
        btn.addEventListener('click', (evt) => {
          const selectedValue = evt.currentTarget.dataset.value;

          // Remove 'active' class from the currently active button
          this.querySelector('.metal-selection-btn.active')?.classList.remove('active');
          evt.currentTarget.classList.add('active');

          if(selectedValue == 'gold'){
            // Show/hide swatch inputs based on the selected metal type
            swatchInputs.forEach(input => {
              input.classList.toggle('d-none', input.dataset.metaltype !== selectedValue);
            });
            const visibleInputs = Array.from(swatchInputs).filter(input => !input.classList.contains('d-none'));
            visibleInputs[0]?.querySelector('.swatch-label')?.click();
          }else{
            swatchInputs.forEach(input => {
              if (input.dataset.metaltype != selectedValue) {
                input.classList.add('d-none');
              }
            });
            this.querySelector(`.metal-swatch [data-metaltype="${selectedValue}"] .swatch-label`)?.click();
          }
        });
      });
    }
}
customElements.define('variant-selects', VariantSelects);